import { api } from './api';
import { trackPushPermissionState } from './analytics';

/**
 * Request browser push permission + register subscription with backend.
 * Returns 'granted' | 'denied' | 'unsupported'.
 */
export async function subscribeToPush(): Promise<'granted' | 'denied' | 'unsupported'> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return 'unsupported';
  }

  const permission = await Notification.requestPermission();
  trackPushPermissionState(permission);

  if (permission !== 'granted') {
    return 'denied';
  }

  try {
    const registration = await navigator.serviceWorker.ready;

    // Fetch VAPID public key from backend
    const res = await fetch('/api/notifications/vapid-key');
    if (!res.ok) return 'unsupported';
    const { vapidPublicKey } = await res.json();
    if (!vapidPublicKey) return 'unsupported';

    // Convert VAPID key to ArrayBuffer for PushManager
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey).buffer as ArrayBuffer;

    // Subscribe via browser Push API
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });

    const subJson = subscription.toJSON();

    // Register with backend
    await api.notifications.registerPush({
      endpoint: subJson.endpoint!,
      keys: {
        p256dh: subJson.keys!.p256dh!,
        auth: subJson.keys!.auth!,
      },
      platform: 'web' as const,
    });

    return 'granted';
  } catch (err) {
    console.error('Push subscription failed:', err);
    return 'denied';
  }
}

/**
 * Unsubscribe from push notifications.
 */
export async function unsubscribeFromPush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      await api.notifications.unregisterPush(endpoint);
    }
  } catch (err) {
    console.error('Push unsubscribe failed:', err);
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
